/**
 * MatchProfileView — Read-only profile view for a matched user.
 *
 * Feb 27 2026: added so users can tap the header avatar in Chat and see the
 * full profile of the person they're talking to. Server-side `/profile/{user_id}`
 * already strips precise GPS + voice-prompt transcript when the viewer is not
 * the owner (see routes/profile.py:894 SEC-002 fix).
 */
import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '@/contexts/AuthContext';
import { ArrowLeft, Loader2 } from 'lucide-react';
import FullProfileBody from '@/components/FullProfileBody';
import VerifiedBadge from '@/components/VerifiedBadge';
import DemoProfileBadge from '@/components/DemoProfileBadge';

export default function MatchProfileView() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/profile/${userId}`);
        if (alive) setProfile(r.data);
      } catch (e) {
        if (alive) setError(e.response?.data?.detail || 'Profile unavailable');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [userId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center" data-testid="match-profile-loading">
        <Loader2 className="w-7 h-7 animate-spin text-[#EF4444]" />
      </div>
    );
  }

  if (error || !profile) {
    return (
      <div className="min-h-screen bg-white" data-testid="match-profile-error">
        <div className="sticky top-0 z-40 bg-white/95 backdrop-blur-xl border-b border-black/[0.06] px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate(-1)} data-testid="match-profile-back" className="text-zinc-500 hover:text-black">
            <ArrowLeft size={20} />
          </button>
          <h1 className="font-brand text-lg font-semibold">Profile</h1>
        </div>
        <div className="flex items-center justify-center h-[60vh]">
          <p className="text-sm text-zinc-500">{error || 'Profile unavailable'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pb-16" data-testid="match-profile-page">
      <div className="sticky top-0 z-40 bg-white/95 backdrop-blur-xl border-b border-black/[0.06] px-4 py-3">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} data-testid="match-profile-back" className="text-zinc-500 hover:text-black">
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1 min-w-0 flex items-center gap-1.5">
            <p className="font-semibold text-sm truncate" data-testid="match-profile-name">
              {profile.display_name}
              {profile.age ? `, ${profile.age}` : ''}
            </p>
            <VerifiedBadge badges={profile.badges} size={14} />
            {profile.is_seed && <DemoProfileBadge isSeed={true} size="xs" />}
          </div>
        </div>
      </div>
      <div className="max-w-lg mx-auto">
        <FullProfileBody profile={profile} distanceKm={null} />
      </div>
    </div>
  );
}
